import {
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentViewModel,
  CaseEventType,
  ExpectedExpenseDto,
  AssignmentModel as OrvalAssignmentModel,
  TranslationLookups,
  UserModel,
} from 'api/model';
import React, { FC, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { toInputDateTimeString, toInputTimeString } from 'utils/date-helpers';
import { AssignmentEditOperation } from '../useQueuedAssignmentUpdates';
import { fakeSelectClassName } from 'components/inputs/SearchableSelect';
import {
  getSelectableStatusOptions,
  statusOptionIsDisabled,
} from 'components/DetailedCase/CaseAssignments/utils';
import { useApiCallFn } from 'hooks/useApiCallFn';
import workHours from 'utils/workHours';
import useModalStack from 'contexts/modal/useModalStack';
import Modal from 'components/Modal';
import PrioritizedCaseAssignmentUserPicker from 'components/PrioritizedCaseAssignmentUserPicker';
import StandardizedComments from 'components/StandardizedComments/StandardizedComments';
import Dropdown from 'components/Dropdown';
import useStandardizedComments from 'contexts/standardizedComments/useStandardizedComments';
import { getUpdatedExpectedExpensesList } from 'utils/expected-expenses';
import EstimatedDurationPicker from 'components/DetailedCase/CaseAssignments/EstimatedDurationPicker';
import {
  useAssignmentGetAssignmentRequests,
  useAssignmentGetHoursWorkedHook,
} from 'api/assignment/assignment';
import OnHoldReasonModal from '../OnHoldReasonModal';
import BookedToChangeReasonModal from '../BookedToChangeReasonModal';
import { bookedToChangeReasonRequired } from 'constants/BookedToChangeReasons';

const StyledUserPicker = styled(PrioritizedCaseAssignmentUserPicker)`
  display: flex;
  height: 100%;

  .${fakeSelectClassName} {
    width: 100%;
    outline: none;
    background-color: ${({ theme }) => theme.colors.background.primary};
    border: 2px solid ${({ theme }) => theme.colors.background.selection};
    border-radius: 0;
    box-shadow: ${({ theme }) => theme.colors.shadow.dropdown};
  }
`;

interface UserPickerCellProps {
  assignmentViewModel: AssignmentViewModel;
  onUpdateAssignment(
    assignmentId: number,
    editOperation: AssignmentEditOperation
  ): void;
}

export const UserPickerCell: FC<UserPickerCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
}) => {
  const modalStack = useModalStack();
  const getHoursWorked = useAssignmentGetHoursWorkedHook();
  const workedHoursCall = useApiCallFn((userId: number) =>
    getHoursWorked({ userId })
  );

  const selectUser = (user: UserModel) => {
    onUpdateAssignment(assignmentViewModel.assignment.assignmentID, (avm) => {
      const currentStatus = avm.assignment.assignmentStatusID;
      let newStatus = avm.assignment.assignmentStatusID;

      // Deselcting user changes status to Created
      if (
        !user &&
        (currentStatus === AssignmentStatusEnum.Planned ||
          currentStatus === AssignmentStatusEnum.Assigned)
      ) {
        newStatus = AssignmentStatusEnum.Created;
      }

      // Selecting user changes status to Planned
      if (
        user &&
        (currentStatus === AssignmentStatusEnum.Created ||
          currentStatus === AssignmentStatusEnum.Assigned)
      ) {
        newStatus = AssignmentStatusEnum.Planned;
      }

      const updatedAvm: AssignmentViewModel = {
        ...avm,
        assignment: {
          ...avm.assignment,
          assignedTo: user ?? undefined,

          assignmentStatusID: newStatus,
        },
      };

      return updatedAvm;
    });
  };

  const handleUserPicked = async (user: UserModel | null) => {
    // startTime is null (not undefined) for un-started assignments in the
    // Orval response, so a strict undefined check would skip the warnings
    if (assignmentViewModel.assignment.startTime) {
      // this assignment has already started, proceeed without warnings
      selectUser(user!);
    } else {
      // this assignment will impact the user's work hours, so check for warnings before proceeding
      const [workedHours] = await workedHoursCall.run(user?.userID ?? -1);
      const workHourWarnings = workHours.getWorkHourWarnings(workedHours);

      if (workHourWarnings) {
        modalStack.push(
          <Modal
            buttons={[
              {
                label: 'Gå vidare ändå',
                onClick: () => {
                  selectUser(user!);
                  modalStack.pop();
                },
              },
              {
                label: 'Ångra',
                onClick: () => modalStack.pop(),
              },
            ]}
            onClose={() => modalStack.pop()}
            title="Varning"
          >
            {`${user?.name ?? 'Fälttestaren'} ${workHourWarnings}`}
          </Modal>
        );
      } else {
        selectUser(user!);
      }
    }
  };

  return (
    <StyledUserPicker
      assignmentId={assignmentViewModel.assignment.assignmentID}
      assignmentType={assignmentViewModel.assignment.assignmentTypeID}
      autoFocus
      bookedTo={assignmentViewModel.assignment.bookedTo.toString()}
      caseModel={assignmentViewModel.case}
      dropdownPosition="left"
      electricCarBufferMs={
        assignmentViewModel.assignment.electricCarBufferTime ?? 0
      }
      estimatedDuration={assignmentViewModel.assignment.estimatedDuration ?? 0}
      estimatedStartTime={
        assignmentViewModel.assignment.estimatedStartTime?.toString() ?? ''
      }
      fromZipCode={assignmentViewModel.assignment.fromZip}
      onUserPicked={handleUserPicked}
      selectedUserId={assignmentViewModel.assignment.assignedTo?.userID ?? null}
    />
  );
};

const baseInputStyling = css`
  margin: 0;
  padding: 0px 3px;
  min-width: 100%;
  min-height: 100%;
  position: relative;
  z-index: 1;

  outline: none;
  border: 2px solid ${({ theme }) => theme.colors.background.selection};
  font: inherit;
  box-shadow: ${({ theme }) => theme.colors.shadow.dropdown};
`;

const InputCell = styled.input<{ width?: number }>`
  ${baseInputStyling}
  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `}
`;

interface BaseInputCellProps {
  type?: React.HTMLInputTypeAttribute;
  initialValue: string;
  onValueSelected(value: string): void;
  width?: number;
}
const BaseInputCell: FC<BaseInputCellProps> = ({
  type,
  initialValue,
  onValueSelected,
  width,
}) => {
  const [input, setInput] = useState(initialValue);

  return (
    <InputCell
      autoFocus
      onChange={(eve) => setInput(eve.target.value)}
      onKeyUp={(eve) => {
        if (eve.key === 'Enter') {
          onValueSelected(input);

          if (eve.target instanceof HTMLElement) {
            eve.target.blur();
          }
        }

        if (eve.key === 'Escape' && eve.target instanceof HTMLElement) {
          eve.target.blur();
        }
      }}
      type={type}
      value={input}
      width={width}
    />
  );
};

interface BaseCellProps {
  assignmentViewModel: AssignmentViewModel;
  onUpdateAssignment(
    assignmentId: number,
    editOperation: AssignmentEditOperation
  ): void;
}

/**
 * Sparar en ny Bokad till först när orsaken är vald. Samma minut som förut är ingen flytt och
 * sparas inte alls.
 */
const useSaveBookedTo = ({
  assignmentViewModel,
  onUpdateAssignment,
}: BaseCellProps) => {
  const modalStack = useModalStack();

  return useCallback(
    (newDate: Date) => {
      const from = new Date(assignmentViewModel.assignment.bookedTo);
      if (
        Math.floor(from.getTime() / 60000) ===
        Math.floor(newDate.getTime() / 60000)
      ) {
        return;
      }

      // Bara huvuduppdraget frågar efter orsak, se bookedToChangeReasonRequired.
      if (
        !bookedToChangeReasonRequired(
          assignmentViewModel.assignment.assignmentTypeID
        )
      ) {
        onUpdateAssignment(
          assignmentViewModel.assignment.assignmentID,
          (avm) =>
            ({
              ...avm,
              assignment: { ...avm.assignment, bookedTo: newDate },
            }) satisfies AssignmentViewModel
        );
        return;
      }

      modalStack.push(
        <BookedToChangeReasonModal
          from={from}
          to={newDate}
          onClose={() => modalStack.pop()}
          onConfirm={({ reason, comment }) => {
            modalStack.pop();
            onUpdateAssignment(
              assignmentViewModel.assignment.assignmentID,
              (avm) => {
                return {
                  ...avm,
                  assignment: {
                    ...avm.assignment,
                    bookedTo: newDate,
                    bookedToChangeReason: reason,
                    bookedToChangeComment: comment,
                  },
                } satisfies AssignmentViewModel;
              }
            );
          }}
        />
      );
    },
    [assignmentViewModel, modalStack, onUpdateAssignment]
  );
};

export const TimePickerCell: FC<BaseCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
}) => {
  const saveBookedTo = useSaveBookedTo({
    assignmentViewModel,
    onUpdateAssignment,
  });

  return (
    <BaseInputCell
      initialValue={toInputTimeString(assignmentViewModel.assignment.bookedTo)}
      onValueSelected={(value) => {
        const [sHour, sMinute] = value.split(':');
        const newDate = new Date(assignmentViewModel.assignment.bookedTo);
        newDate.setHours(Number(sHour));
        newDate.setMinutes(Number(sMinute));
        saveBookedTo(newDate);
      }}
      type="time"
    />
  );
};

export const DateTimePickerCell: FC<BaseCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
}) => {
  const saveBookedTo = useSaveBookedTo({
    assignmentViewModel,
    onUpdateAssignment,
  });

  return (
    <BaseInputCell
      initialValue={toInputDateTimeString(
        assignmentViewModel.assignment.bookedTo
      )}
      onValueSelected={(value) => saveBookedTo(new Date(value))}
      type="datetime-local"
    />
  );
};

export const ExtraInfoCell: FC<BaseCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
}) => (
  <BaseInputCell
    initialValue={assignmentViewModel.assignment.extraInfo}
    onValueSelected={(value) => {
      onUpdateAssignment(
        assignmentViewModel.assignment.assignmentID,
        (avm) =>
          ({
            ...avm,
            assignment: {
              ...avm.assignment,
              extraInfo: value,
            },
          }) satisfies AssignmentViewModel
      );
    }}
    width={300}
  />
);

export const StandardizedCommentsCell: FC<BaseCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
}) => {
  const globalStandardizedComments = useStandardizedComments();

  return (
    <Dropdown
      content={
        <div
          style={{
            maxWidth: 250,
            padding: 5,
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          <StandardizedComments
            assignmentId={assignmentViewModel.assignment.assignmentID}
            commentsOnAssignment={
              assignmentViewModel.assignment.standardizedComments
            }
            onChange={(comments) => {
              // update expected expenses based on change
              const newExpectedExpenses = getUpdatedExpectedExpensesList({
                globalStandardizedComments,
                existingExpectedExpenses:
                  assignmentViewModel.assignment.expectedExpenses.map(
                    (e) => e.type
                  ),
                newComments: comments,
                existingComments:
                  assignmentViewModel.assignment.standardizedComments,
              });

              onUpdateAssignment(
                assignmentViewModel.assignment.assignmentID,
                (avm) =>
                  ({
                    ...avm,
                    assignment: {
                      ...avm.assignment,
                      standardizedComments: comments,
                      expectedExpenses: newExpectedExpenses.map(
                        (e) =>
                          ({
                            assignmentId: -1,
                            type: e,
                            estimatedCost: 0,
                          }) satisfies ExpectedExpenseDto
                      ),
                    },
                  }) satisfies AssignmentViewModel
              );
            }}
          />
        </div>
      }
    >
      {assignmentViewModel.assignment.standardizedComments
        .map((c) => c.comment)
        .join(', ')}
    </Dropdown>
  );
};

const CellSelect = styled.select`
  ${baseInputStyling}
`;

interface StatusPickerCellProps extends BaseCellProps {
  assignmentStatuses: TranslationLookups['assignmentStatuses'];
  onDirectStatusChange?(
    assignmentId: number,
    assignmentViewModel: AssignmentViewModel,
    newStatus: AssignmentStatusEnum,
    eventType: CaseEventType,
    eventMessage: string
  ): void;
}

export const StatusPickerCell: FC<StatusPickerCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
  assignmentStatuses,
  onDirectStatusChange,
}) => {
  const modalStack = useModalStack();
  const assignmentRequests = useAssignmentGetAssignmentRequests({
    assignmentId: assignmentViewModel.assignment.assignmentID,
  });
  const disableOption = useCallback(
    (option: AssignmentStatusEnum) => {
      // disable "Tilldelad" option if assignment has no estimated duration
      if (
        (
        [
          AssignmentStatusEnum.Assigned,
          AssignmentStatusEnum.Accepted,
          AssignmentStatusEnum.Started,
          AssignmentStatusEnum.Arrived,
          AssignmentStatusEnum.TestComplete,
          AssignmentStatusEnum.Inspected,
          AssignmentStatusEnum.Returning,
          AssignmentStatusEnum.Complete,
          ] as AssignmentStatusEnum[]
        ).includes(option) &&
        assignmentViewModel.assignment.estimatedDuration === null
      ) {
        return true;
      }

      return false;
    },
    [assignmentViewModel]
  );

  const openOnHoldModal = useCallback(() => {
    modalStack.push(
      <OnHoldReasonModal
        onConfirm={(reason) => {
          modalStack.pop();
          onDirectStatusChange?.(
            assignmentViewModel.assignment.assignmentID,
            assignmentViewModel,
            AssignmentStatusEnum.OnHold,
            CaseEventType.AssignmentOnHold,
            reason
          );
        }}
        onClose={() => modalStack.pop()}
      />
    );
  }, [assignmentViewModel, modalStack, onDirectStatusChange]);

  return (
    <CellSelect
      autoFocus
      onChange={(eve) => {
        const newStatus = Number(eve.target.value) as AssignmentStatusEnum;

        if (newStatus === AssignmentStatusEnum.OnHold) {
          openOnHoldModal();
          if (eve.target instanceof HTMLElement) {
            eve.target.blur();
          }
          return;
        }

        onUpdateAssignment(
          assignmentViewModel.assignment.assignmentID,
          (avm) =>
            ({
              ...avm,
              assignment: {
                ...avm.assignment,
                assignmentStatusID: newStatus,
              },
            }) satisfies AssignmentViewModel
        );

        if (eve.target instanceof HTMLElement) {
          eve.target.blur();
        }
      }}
      onKeyUp={(eve) => {
        if (eve.key === 'Escape' && eve.target instanceof HTMLElement) {
          eve.target.blur();
        }
      }}
      value={assignmentViewModel.assignment.assignmentStatusID}
    >
      {getSelectableStatusOptions(
        assignmentViewModel.assignment.assignmentStatusID,
        assignmentViewModel.case.caseTypeID,
        // NSwag/Orval-bro: utils.ts förväntar Orval AssignmentModel.
        assignmentViewModel.assignment as unknown as OrvalAssignmentModel
      ).map((option) => (
        <option
          disabled={
            disableOption(option) ||
            statusOptionIsDisabled({
              currentAssignmentStatus: option,
              events: assignmentRequests.data ?? [],
              currentAssignedToId:
                assignmentViewModel.assignment.assignedTo?.userID,
              currentAssignmentId: assignmentViewModel.assignment.assignmentID,
              drivingPolicyAssignmentInNeedOfHandling:
                assignmentViewModel.assignment
                  .drivingPolicyAssignmentInNeedOfHandling,
            })
          }
          key={option}
          value={option}
        >
          {assignmentStatuses[option]}
          {disableOption(option) && ' (ange först estimerad tid)'}
        </option>
      ))}
    </CellSelect>
  );
};

interface EstimatedDurationCellProps extends BaseCellProps {
  placeholder?: string;
}

export const EstimatedDurationCell: FC<EstimatedDurationCellProps> = ({
  assignmentViewModel,
  onUpdateAssignment,
  placeholder,
}) => {
  const [updatedAssignment, setUpdatedAssignment] = useState<AssignmentModel>(
    assignmentViewModel.assignment
  );

  return (
    <EstimatedDurationPicker
      // NSwag/Orval-bro: EstimatedDurationPicker förväntar Orval AssignmentModel.
      assignment={updatedAssignment as unknown as OrvalAssignmentModel}
      caseType={assignmentViewModel.case.caseTypeID}
      isElectric={assignmentViewModel.case.isElectric === true}
      hideSelectAverageCheckbox
      onChangeEstimatedDuration={(value) => {
        onUpdateAssignment(
          assignmentViewModel.assignment.assignmentID,
          (avm) => {
            return {
              ...avm,
              assignment: {
                ...updatedAssignment,
                estimatedDuration: value,
              },
            } satisfies AssignmentViewModel;
          }
        );
      }}
      onChangeSystemEstimatedDurationApprox={(value) => {
        setUpdatedAssignment({
            ...updatedAssignment,
            systemEstimatedDurationApprox: value,
        });
      }}
      onChangeSystemEstimatedDurationExact={(value) => {
        setUpdatedAssignment({
            ...updatedAssignment,
            systemEstimatedDurationExact: value,
        });
      }}
      placeholder={placeholder}
    />
  );
};
